import {
    DiagramEngine,
    DiagramModel,
    DefaultNodeModel,
    DefaultLinkModel,
    DiagramWidget,
    BaseEvent,
    BaseModel,
    BaseEntity,
    BaseListener,
    BaseModelListener,
    LinkModel,
    LinkModelListener,
    NodeModel
} from "storm-react-diagrams";
import * as React from "react";
import { Prompt } from "react-router";
import domtoimage from "dom-to-image";

import "./designer.css";

import Toolbox from "./toolbox";
import { deserialize, serialize } from "./serializer";
import { createWidget, changeWidget } from "./widgets";
import { CustomNodeFactory } from "./widgets/CustomNodeWidgetFactory";
import i18n from "../i18n";
import { ScenarioModel } from "../models/scenario";
import { getAsync, postAsync } from "../utils/net";
import { CustomLinkFactory } from "./widgets/CustomLinkFactory";
import { Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faToggleOn, faToggleOff } from "@fortawesome/free-solid-svg-icons";
import { settings } from "../settings";
import ShowHistory from "./show-history";
import ShowSearch from "./show-search";
import { checkUser } from "../security/security";
import { ToastsStore } from "react-toasts";
import { ModalNotification } from "../utils/modalNotification";

interface DesignerState {
    selectedElement?: DefaultNodeModel | DefaultLinkModel | ScenarioModel | null;
    selectedItems: any[];
    engine: DiagramEngine;
    currentScenario: ScenarioModel | null;
    originalScenario: ScenarioModel;
    isNewScenario: boolean;
    hasChanges: boolean;
    originalStartNodes: string[];
    showToolbox: boolean;
    scenarios: any[];
    showHistory: boolean;
    showSearch: boolean;
    locked: boolean;
    showModal: boolean;
    modalMessage: string;
    modalResolve?: () => void;
}

export default class Designer extends React.Component<{}, DesignerState> {
    constructor(props: any) {
        super(props);
        // 1) setup the diagram engine
        const engine = new DiagramEngine();
        engine.installDefaultFactories();
        engine.registerNodeFactory(new CustomNodeFactory());
        engine.registerLinkFactory(new CustomLinkFactory());

        const model = new DiagramModel();
        engine.setDiagramModel(model);
        model.setLocked(settings.disableScenarioSave);
        model.addListener({
            nodesUpdated: ev => this.onNodeUpdate(ev),
            linksUpdated: ev => this.onLinkUpdate(ev)
        });
        const newBlankScenario = this.createNewBlankScenario();
        this.state = {
            engine,
            currentScenario: newBlankScenario,
            originalScenario: newBlankScenario,
            isNewScenario: true,
            selectedItems: [],
            hasChanges: false,
            selectedElement: newBlankScenario,
            originalStartNodes: [],
            showToolbox: true,
            scenarios: [],
            showHistory: false,
            showSearch: false,
            locked: false,
            showModal: false,
            modalMessage: ""
        };

        this.showModal = this.showModal.bind(this);
        this.hideModal = this.hideModal.bind(this);

        this.loadScenarioList();
    }

    private createNewBlankScenario() {
        return {
            id: "",
            virtualNode: "",
            category: "",
            channels: "",
            definition: "",
            entities: "",
            intents: "",
            keywords: "",
            scenarioDescription: "",
            languages: "",
            checkedOutBy: "",
            rowKey: "",
            partitionKey: "",
            // timestamp: "",
        };
    }

    componentDidMount() {
        // tslint:disable max-line-length
        // See https://stackoverflow.com/questions/22092762/how-to-detect-ctrlc-and-ctrlv-key-pressing-using-regular-expression
        document.body.addEventListener(
            "keydown",
            e => {
                e = e || window.event;
                const key = e.which || e.keyCode; // keyCode detection
                const ctrl = e.ctrlKey ? e.ctrlKey : key === 17 ? true : false; // ctrl detection

                if (key === 86 && ctrl) {
                    this.paste();
                } else if (key === 67 && ctrl) {
                    this.copy();
                }
            },
            false
        );

        document.body.addEventListener("keyup", e => this.onKeyUp(e), false);
    }

    private showModal(message: string): Promise<void> {
        return new Promise((resolve) => {
            this.setState({ showModal: true, modalMessage: message, modalResolve: resolve });
        });
    }

    private hideModal() {
        this.setState({ showModal: false, modalMessage: "" }, () => {
            if (this.state.modalResolve) {
                this.state.modalResolve();
                this.setState({ modalResolve: undefined });
            }
        });
    }

    render() {
        this.state.engine.getDiagramModel().setLocked(this.state.locked);

        return (
            <div className="grid-bg">
                <Prompt
                    when={this.state.hasChanges}
                    message={location =>
                        `Your changes will be lost. Are you sure to leave?`
                    }
                />
                {this.state.currentScenario && (
                    <ShowHistory
                        isOpen={this.state.showHistory}
                        onClose={() => this.setState({ showHistory: false })}
                        onLoadHistory={historyItem => this.onLoadHistory(historyItem)}
                        scenarioName={this.state.currentScenario.partitionKey}
                    />
                )}
                <ShowSearch
                    isOpen={this.state.showSearch}
                    onClose={() => this.setState({ showSearch: false })}
                    onSearchItemSelected={(scenarioName) => this.onSearchItemSelected(scenarioName)}
                />
                <Button
                    color="danger"
                    className="toolbox-toggle"
                    onClick={() => this.toggleToolbox()}
                >
                    <FontAwesomeIcon
                        icon={this.state.showToolbox ? faToggleOn : faToggleOff}
                    />
                </Button>
                <Toolbox
                    currentScenario={this.state.currentScenario}
                    currentScenarioHasChanges={this.state.hasChanges}
                    locked={this.state.locked}
                    scenarios={this.state.scenarios}
                    show={this.state.showToolbox}
                    selectedElement={this.state.selectedElement}
                    currentScenarioModel={this.state.engine.getDiagramModel()}
                    isNewScenario={this.state.isNewScenario}
                    onNewNode={s => this.onNewNode(s)}
                    onChangeNodeType={s => this.onChangeNodeType(s)}
                    onNewScenario={() => this.onNewScenario()}
                    onItemUpdate={(f, v) => this.onItemUpdate(f, v)}
                    onEditJson={json => this.onEditJson(json)}
                    onOpenScenario={name => this.loadModel(name)}
                    onSaveScenario={() => this.onSaveScenario()}
                    onUpdateScenario={model => this.onUpdateScenario(model)}
                    onZoomToFit={() => this.state.engine.zoomToFit()}
                    onCopyAsImage={() => this.onCopyAsImage()}
                    onShowHistory={() => this.setState({ showHistory: true })}
                    onCheckoutScenario={() => this.onCheckoutScenario()}
                    onCheckinScenario={() => this.onCheckinScenario()}
                    onReloadLatest={() => this.onReloadLatest()}
                    onSearch={() => this.setState({ showSearch: true })}
                />
                <DiagramWidget
                    className="srd-demo-canvas"
                    diagramEngine={this.state.engine}
                    smartRouting={false}
                    allowLooseLinks={false}
                    maxNumberPointsPerLink={0}
                    deleteKeys={[]}
                />
                <ModalNotification
                    modalMessage={this.state.modalMessage}
                    showModal={this.state.showModal}
                    onClose={this.hideModal}
                />
            </div>
        );
    }

    private toggleToolbox() {
        this.setState({
            showToolbox: !this.state.showToolbox
        });
    }

    private async onLoadHistory(historyItem: any) {
        this.setState({ showHistory: false });

        // Make sure when we are loading from history, the loaded version still stays checked out:
        if (this.state.currentScenario !== null) {
            let checkedOutBy = this.state.currentScenario.checkedOutBy;

            await this.loadModel(historyItem);

            let currentScenario = this.state.currentScenario;
            currentScenario.checkedOutBy = checkedOutBy;

            this.setState({
                currentScenario: currentScenario,
                hasChanges: true,
                locked: false,
            });
        }
    }

    private onSearchItemSelected(scenario: any) {
        const scenarioObj: ScenarioModel = {
            id: scenario.id,
            partitionKey: scenario.partitionKey,
            rowKey: scenario.rowKey,
            virtualNode: scenario.virtualNode,
            category: scenario.category,
            channels: scenario.channels,
            definition: scenario.definition,
            entities: scenario.entities,
            intents: scenario.intents,
            keywords: scenario.keywords,
            timestamp: scenario.timestamp,
            changedBy: scenario.changedBy,
            checkedOutBy: scenario.checkedOutBy,
            scenarioDescription: scenario.scenarioDescription,
            languages: scenario.languages
        };
        this.setState({ showSearch: false });
        this.loadModel(scenarioObj);
    }

    // See original at https://github.com/projectstorm/react-diagrams/blob/master/src/widgets/DiagramWidget.tsx#L300
    onKeyUp(event: KeyboardEvent) {
        // delete all selected
        if (
            event.keyCode === 46 &&
            (document.activeElement &&
                !/INPUT|TEXTAREA/i.test(document.activeElement.nodeName))
        ) {
            this.state.engine
                .getDiagramModel()
                .getSelectedItems()
                .forEach(element => {
                    // only delete items which are not locked
                    if (!this.state.engine.isModelLocked(element)) {
                        element.remove();
                    }
                });
            this.forceUpdate();
        }
    }

    private async onCopyAsImage() {
        const { currentScenario } = this.state;

        const area = document.querySelector(".srd-demo-canvas") as HTMLElement;
        if (area && currentScenario) {
            area.style.overflow = "visible";
            const dataUrl = await domtoimage.toPng(area, {
                bgcolor: "#777"
            });
            area.style.overflow = "";
            var link = document.createElement("a");
            link.download = `${currentScenario.partitionKey}.png`;
            link.href = dataUrl;
            link.click();
        }
    }

    private selectedNodeChanged(
        ev: BaseEvent<BaseModel<BaseEntity<BaseListener<any>>, BaseModelListener>>
    ) {
        if (window.event && (window.event as KeyboardEvent).shiftKey) {
            return;
        }
        const node = ev.entity as DefaultNodeModel;
        this.setState({
            selectedElement: node.selected ? node : this.state.currentScenario
        });
    }

    private selectedLinkChanged(
        ev: BaseEvent<
            BaseModel<BaseEntity<BaseListener<any>>, BaseModelListener>
        > & {
            isSelected: boolean;
        }
    ) {
        if (window.event && (window.event as KeyboardEvent).shiftKey) {
            return;
        }
        const link = ev.entity as DefaultLinkModel;
        this.setState({
            selectedElement: link.selected ? link : this.state.currentScenario
        });
    }

    private onNewNode(type: string) {
        const model = this.state.engine.getDiagramModel();
        const newNode = createWidget(model, type);
        if (newNode) {
            const offsetX = this.state.engine.diagramModel.offsetX;
            const offsetY = this.state.engine.diagramModel.offsetY;
            newNode.setPosition(
                window.innerWidth / 2 - offsetX,
                window.innerHeight / 2 - offsetY
            );
            newNode.addListener({
                selectionChanged: ev => this.selectedNodeChanged(ev)
            });
            this.setState({
                hasChanges: true,
                locked: false,
            });
        }
        this.forceUpdate();
    }
    private onChangeNodeType(type: string) {
        const { selectedElement } = this.state;
        if (selectedElement && selectedElement instanceof DefaultNodeModel) {
            changeWidget(selectedElement, type);
            this.setState({ hasChanges: true });
        }
        this.forceUpdate();
    }

    private onNodeUpdate(ev: any) {
        this.saveModelDraft();
    }

    private onItemUpdate(field: any, value: any) {
        this.saveModelDraft();

        if (this.state.selectedElement instanceof DefaultLinkModel) {
            if (field.name === "choice") {
                const link = this.state.selectedElement as DefaultLinkModel;
                link.labels.pop();
                link.addLabel(value);
            }
        }
    }

    private onLinkUpdate(
        ev: BaseEvent<any> & {
            link: LinkModel<LinkModelListener>;
            isCreated: boolean;
        }
    ) {
        this.saveModelDraft();
        const model = ev.entity as DiagramModel;
        Object.keys(model.links)
            .map(k => model.links[k])
            // .filter(l => !l.listeners.selectionChanged)
            .forEach(l =>
                l.addListener({
                    selectionChanged: ev2 => this.selectedLinkChanged(ev2)
                })
            );
    }

    private saveModelDraft() {
        this.setState({ hasChanges: true });
    }

    private async loadModel(scenario: ScenarioModel | null) {
        if (
            this.state.hasChanges &&
            !confirm(i18n.Designer.Toolbox.DiscardChanges)
        ) {
            return;
        }

        if (scenario) {
            const reply = await getAsync(
                `/api/designer/getscenario/${scenario.id}?version=${scenario.rowKey}`
            );

            const payload = await reply.json();
            scenario.changedBy = payload.ChangedBy;
            scenario.checkedOutBy = payload.CheckedOutBy;

            //    const text = await reply.text();
            this.deserializeJson(scenario, payload.Definition);
        } else {
            ToastsStore.error("Failed to load scenario");
        }
    }

    private async loadScenarioList() {
        const reply = await getAsync("/api/designer/GetScenariosList");
        const payload = await reply.json();
        this.setState({
            scenarios: payload
        });
    }

    private deserializeJson(scenario: ScenarioModel, json: string) {
        const model = deserialize(json);
        this.state.engine.setDiagramModel(model);

        model.addListener({
            nodesUpdated: ev => this.onNodeUpdate(ev),
            linksUpdated: ev => this.onLinkUpdate(ev)
        });

        // Need to re-add all event listeners to nodes...
        const nodes = model.getNodes();
        const startNodes = this.getStartNodes(model);
        Object.keys(nodes)
            .map(s => nodes[s])
            .forEach(n => {
                n.addListener({
                    selectionChanged: ev => this.selectedNodeChanged(ev)
                });
            });

        // ...and links
        const links = model.getLinks();
        Object.keys(links)
            .map(s => links[s])
            .forEach(n =>
                n.addListener({
                    selectionChanged: ev => this.selectedLinkChanged(ev)
                })
            );

        const originalStartNodes = startNodes.map(n => n.id);

        let originalScenario = scenario;
        if (scenario != null && scenario.status != null && scenario.status === "History") {
            originalScenario = this.state.originalScenario;
        }
        this.setState({
            isNewScenario: false,
            currentScenario: scenario,
            originalScenario: originalScenario,
            selectedElement: scenario,
            locked: !scenario.checkedOutBy || !checkUser(scenario.checkedOutBy),
            originalStartNodes
        });
    }

    private async onReloadLatest() {
        let latestVersionOfCurrentScenario = this.state.scenarios.find(
            s => this.state.currentScenario !== null && this.state.originalScenario !== null && s.id === this.state.originalScenario.id);

        await this.loadModel(latestVersionOfCurrentScenario);
        this.setState({ hasChanges: false });
    }

    private async onSaveScenario() {
        if (!confirm("Are you sure to save?")) {
            return;
        }

        const model = this.state.engine.getDiagramModel();
        const startNodes = this.getStartNodes(model);
        const currentStartNodes = startNodes.map(n => n.id);
        if (!currentStartNodes.length) {
            await this.showModal(i18n.Designer.Toolbox.WarnNoStartNodes);
            return;
        }
        if (
            !(
                currentStartNodes.length === this.state.originalStartNodes.length &&
                currentStartNodes.every(
                    n => this.state.originalStartNodes.indexOf(n) > -1
                )
            )
        ) {
            await this.showModal(i18n.Designer.Toolbox.WarnStartNodesChanged);
        }

        if (startNodes.length > 1) {
            if (
                !confirm(
                    `The following scenarios will ALSO be modified:\n${startNodes
                        .map(n => `* ${n.extras.q}`)
                        .join("\n")}\n\nDo you wish to proceed?`
                )
            ) {
                return;
            }
        }

        const definition = serialize(model);
        const json = JSON.stringify(definition);

        const looseNodes = definition
            .filter(n => n.nextq === "-1")
            .filter(
                n =>
                    n.type !== "MessageEnd" &&
                    n.type !== "CallScenario" &&
                    n.type !== "CallScenarioByIntent" &&
                    n.type !== "CallQnA"
            );
        if (looseNodes.length > 0) {
            const details = looseNodes
                .map(n => `* [${n.type}] ${(n.q || "").slice(0, 10)}`)
                .join("\n");
            await this.showModal(
                `Every branch of the graph needs to end either a CallScenario/CallScenarioByIntent/CallQnA/MessageEnd block. Please check:\n\n${details}`
            );
            return;
        }

        if (json.length > 2 * 1024 * 1024) {
            await this.showModal(i18n.Designer.Toolbox.ExceededTechnicalLimit);
            return;
        } else if (json.length > 1.5 * 1024 * 1024) {
            await this.showModal(
                `Definition size is approaching the technical limit (currently ${json.length
                }). Please consider splitting this scenario.`
            );
        }

        let response = await postAsync("/api/designer/SaveDefinition", {
            ...this.state.currentScenario,
            definition: json
        });

        let result = await response.json();

        if (result.success) {
            const savedScenario = result.content as ScenarioModel

            this.setState(prevState => ({
                currentScenario: {
                    ...prevState.currentScenario!,
                    partitionKey: savedScenario.partitionKey,
                    id: savedScenario.id
                }
            }))
            ToastsStore.success("Everything was saved!");
        } else {
            ToastsStore.error(result.message);
        }

        this.setState({
            hasChanges: false
        });

        if (!this.state.isNewScenario) {
            await this.onReloadLatest();
        }
    }
    private copy() {
        const { engine } = this.state;
        const model = engine.getDiagramModel();
        const selectedItems = model.getSelectedItems();
        this.setState({
            selectedItems
        });
    }
    private paste() {
        if (
            document.activeElement &&
            /INPUT|TEXTAREA/i.test(document.activeElement.nodeName)
        ) {
            return;
        }

        const { engine, selectedItems } = this.state;
        let offset = { x: 100, y: 100 };
        let model = engine.getDiagramModel();

        let itemMap = {};
        selectedItems.forEach((item: BaseModel<any>) => {
            let newItem = item.clone(itemMap);
            // Very important, we need deep cloning!
            newItem.extras = { ...newItem.extras };

            // offset the nodes slightly
            if (newItem instanceof NodeModel) {
                newItem.setPosition(newItem.x + offset.x, newItem.y + offset.y);
                newItem.extras.id = newItem.id;
                newItem.removeListener("selectionChanged");
                newItem.addListener({
                    selectionChanged: ev => this.selectedNodeChanged(ev)
                });
                model.addNode(newItem);
            } else if (newItem instanceof LinkModel) {
                // offset the link points
                newItem.getPoints().forEach(p => {
                    p.updateLocation({ x: p.getX() + offset.x, y: p.getY() + offset.y });
                });
                newItem.removeListener("selectionChanged");
                newItem.addListener({
                    selectionChanged: (ev: any) => this.selectedNodeChanged(ev)
                });
                model.addLink(newItem);
            }
            newItem.selected = false;
        });

        this.forceUpdate();
    }

    private onNewScenario() {
        if (
            this.state.hasChanges &&
            !confirm(i18n.Designer.Toolbox.DiscardChanges)
        ) {
            return;
        }
        var model = new DiagramModel();
        this.state.engine.setDiagramModel(model);
        model.setLocked(settings.disableScenarioSave);
        model.addListener({
            nodesUpdated: ev => this.onNodeUpdate(ev),
            linksUpdated: ev => this.onLinkUpdate(ev)
        });
        const newScenario = this.createNewBlankScenario();
        this.setState({
            isNewScenario: true,
            currentScenario: newScenario,
            selectedElement: newScenario,
            hasChanges: false
        });
    }

    private onUpdateScenario(model: ScenarioModel) {
        this.setState({
            currentScenario: model,
            selectedElement: model
        });
    }

    private getStartNodes(model: DiagramModel) {
        return Object.keys(model.getNodes())
            .map(k => model.nodes[k])
            .filter(n => n.extras.type === "Start");
    }

    private onEditJson(json: string) {
        if (this.state.currentScenario) {
            this.deserializeJson(this.state.currentScenario, json);
            this.setState({
                isNewScenario: true,
                locked: false
            });
        }
    }

    private async onCheckoutScenario() {
        // Make sure that the user is aware of the fact that this scenario is being edited by another user or not:
        await this.loadModel(this.state.currentScenario);

        let confirmMessage = "Do you wish to checkout this scenario and ALL related start nodes?";
        if (this.state.currentScenario !== null && this.state.currentScenario.checkedOutBy
            && !checkUser(this.state.currentScenario.checkedOutBy)) {
            confirmMessage =
                "WARNING: THERE IS ANOTHER USER (" + this.state.currentScenario.checkedOutBy
                + ") ALREADY WORKING ON THIS SCENARIO!\n" + confirmMessage;
        }
        if (!confirm(confirmMessage)) {
            return;
        }

        const reply = await postAsync(
            "/api/designer/checkout",
            this.state.currentScenario
        );
        const updatedModel = await reply.json();

        await this.loadScenarioList();

        this.setState({
            currentScenario: updatedModel,
            selectedElement: updatedModel,
            locked: false,
        });

        ToastsStore.success("Checkout completed!");
    }

    private async onCheckinScenario() {
        if (!confirm("Are you sure to checkin this scenario and ALL related start nodes?")) {
            return;
        }

        const reply = await postAsync(
            "/api/designer/checkin",
            this.state.currentScenario
        );
        const updatedModel = await reply.json();

        await this.loadScenarioList();

        this.setState({
            currentScenario: updatedModel,
            selectedElement: updatedModel,
            locked: true,
        });

        ToastsStore.success("Checkin completed!");
    }
}



// WEBPACK FOOTER //
// ./src/designer/designer.tsx